import React, {ChangeEvent, Component, ReactElement, ReactNode, RefObject} from 'react'
import {AdminPagePropsSet, createAdminPage} from '../../../components/shared/hoc/createAdminPage'
import NatDisasterRepo from './repository/NatDisasterRepo'
import {Nullable} from '../../../app/TypeHelpers'
import {SessionStorageHelper} from '../../../SessionStorageHelper'
import {AppAdminContext} from '../../../app/AppAdminContext'
import NatCondominiumRepo from '../condominium/repository/NatCondominiumRepo'
import EventEmitterExAdmin from '../../../components/shared/pageBaseComponents/EventEmitterExAdmin'
import {
    PageFooterArea,
    PageFotterAreaLeft,
    PageFotterAreaRight,
    PageMainArea
} from '../../../components/shared/hoc/withAdminPageTemplate'
import {Box, Link, Table, TableBody, TableRow, TableCell} from '@mui/material'
import {StyledTableCaption, TableRowName} from '../../../styledComponents/adminPage/Table'
import {ADMINISTORATOR_MODE, DISASTER_RELEASE_SETTING, MESSAGE_TYPE} from '../../../CodeMaster'
import PPTextField from '../../../components/shared/forms/PPTextField'
import PPHtmlEditor from '../../../components/shared/forms/PPHtmlEditor'
import {FlexStartRowFlexContainer, RowFlexContainer} from '../../../styledComponents/FlexComponents'
import PPFileInput from '../../../components/shared/forms/PPFileInput'
import {AdminPrimaryButton} from '../../../styledComponents/adminPage/Buttons'
import {Messages} from '../../../Messages'
import PPRadioGroup from '../../../components/shared/forms/PPRadioGroup'
import styled from 'styled-components'
import {StyledDiv, StyledLabel} from '../../../styledComponents/StyledTypeDefinitions'
import {HeadingAreaMessageParams} from '../../../components/shared/pageBaseComponents/HeadingAreaMessageComponent'
import {HKConstants} from '../../../HKConstants'
import {openPdfPreviewWindow} from '../../common/util/Natutil'
import PPNumericField from '../../../components/shared/forms/PPNumericField'
import {BackLink, DeleteLink} from '../../../styledComponents/adminPage/Links'
import {NatGetCondominium} from '../condominium/repository/NatGetCondominium'
import {
    DisasterPreventionEditFile,
    NatPutDisasterPreventionEditParams
} from './repository/NatPutDisasterPreventionEdit'
import {
    NatPostDisasterPreventionFileRequest,
    NatPostDisasterPreventionFileResponse
} from './repository/NatPostDisasterPreventionFile'
import {RequestItemError} from '../../../network/response/RequestItemError'
import {NatFileRepo} from '../files/repository/NatFileRepo'
import {ValidateUploadFile} from '../files/repository/NatFileNameValidate'
import {Colors} from '../../../styledComponents/Constants'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faFilePdf} from '@fortawesome/free-solid-svg-icons'
import {Location} from 'react-router-dom'

const SELECTED_ADMINISTORATOR_MODE: string = 'selectedMode'

interface Props {
    condominiumCd: string
    disasterPreventionId: number
    condominiumRepo: NatCondominiumRepo
    disasterRepo: NatDisasterRepo
    fileRepo: NatFileRepo
    eventEmitter: EventEmitterExAdmin
    location: Location
}

interface States {
    condominiumName: string
    title: string
    description: string
    releaseSetting: string
    version: number
    disasterPreventionFileList: DisasterPreventionFileList[]
    fileName: string,
    fileData: string | ArrayBuffer | null
    publishMaterialName: string
    registerButtonDisabled: boolean
    fileInputDisabled: boolean
    addFileButtonDisabled: boolean
    isLoadCompleted: boolean
}

interface DisasterPreventionFileList extends DisasterPreventionEditFile {
    fileName: string
    fileData: string | ArrayBuffer | null
    path?: string
    registeredFlag: boolean
    deleteFlag: boolean
}

class NatAdminDisasterPreventionEditPage extends Component<Props & AdminPagePropsSet, States> {
    mode: Nullable<string>
    disasterPreventionFileRef: RefObject<HTMLInputElement>;

    constructor(props: Props & AdminPagePropsSet) {
        super(props);
        this.mode = SessionStorageHelper.getStringItemOrNull(SELECTED_ADMINISTORATOR_MODE);
        this.state = {
            condominiumName: '',
            title: '',
            description: '',
            releaseSetting: '',
            version: 0,
            disasterPreventionFileList: [],
            fileName: '',
            fileData: '',
            publishMaterialName: '',
            registerButtonDisabled: false,
            fileInputDisabled: false,
            addFileButtonDisabled: true,
            isLoadCompleted: false
        }
        this.disasterPreventionFileRef = React.createRef()
        this.handleChangeText = this.handleChangeText.bind(this)
        this.handleChangeDescription = this.handleChangeDescription.bind(this)
        this.handleChangeReleaseSetting = this.handleChangeReleaseSetting.bind(this)
        this.handleChangePublishMaterialName = this.handleChangePublishMaterialName.bind(this)
        this.handleChangePublishName = this.handleChangePublishName.bind(this)
        this.handleChangeSortOrder = this.handleChangeSortOrder.bind(this)
        this.handleChangeFile = this.handleChangeFile.bind(this);
        this.addFileButton = this.addFileButton.bind(this);
        this.registerButton = this.registerButton.bind(this)
        this.handleRegisterOkClick = this.handleRegisterOkClick.bind(this)
        this.handleRegisterCancelClick = this.handleRegisterCancelClick.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            const {condominiumCd, disasterPreventionId, condominiumRepo, disasterRepo, eventEmitter} = this.props

            // 全体モードの場合は物件名を表示するために物件情報を取得する
            if (this.mode === ADMINISTORATOR_MODE.ALL.value) {
                const condominiumResponse: Nullable<NatGetCondominium> = await this.props.asyncCallFunction(
                    condominiumRepo.getCondominium.bind(condominiumRepo, condominiumCd),
                    (httpErrorOrNetworkError) => {
                        eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                    }
                )
                if (condominiumResponse?.details) {
                    this.setState({
                        condominiumName: condominiumResponse.details.condominiumName
                    })
                }
            }

            // 防災情報取得
            const response = await this.props.asyncCallFunction(
                disasterRepo.getDisasterInfo.bind(disasterRepo, condominiumCd, disasterPreventionId),
                (httpErrorOrNetworkError) => {
                    eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                }
            )
            if (response?.details) {
                const disasterPreventionFileList = response.details.disasterPreventionFile?.map(file => {
                    return {
                        fileName: file.fileName,
                        fileData: '',
                        publishMaterialName: file.publishMaterialName,
                        sortOrder: file.sortOrder,
                        fileNumber: file.fileNumber,
                        path: file.path,
                        registeredFlag: true,
                        deleteFlag: false
                    }
                })

                this.setState({
                    title: response.details.title,
                    description: response.details.description,
                    releaseSetting: response.details.releaseSetting,
                    version: response.details.version,
                    disasterPreventionFileList: disasterPreventionFileList ?? [],
                    fileInputDisabled: (disasterPreventionFileList?.length ?? 0) >= 5
                })
            }
        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({isLoadCompleted: true})
        }
    }

    handleChangeText(event: ChangeEvent<HTMLInputElement>): void {
        const key = event.target.name as keyof States
        const value: string = event.target.value
        this.setState({...this.state, [key]: value}, () => {
            this.updateRegisterButtonDisabled()
        })
    }

    handleChangeDescription(value: string): void {
        this.setState({description: value}, () => {
            this.updateRegisterButtonDisabled()
        })
    }

    handleChangeReleaseSetting(event: ChangeEvent<HTMLInputElement>): void {
        this.setState({releaseSetting: event.target.value}, () => {
            this.updateRegisterButtonDisabled()
        })
    }

    handleChangePublishMaterialName(event: React.ChangeEvent<HTMLInputElement>): void {
        if (event.target.value && this.state.fileName) {
            this.setState({addFileButtonDisabled: false});
        } else {
            this.setState({addFileButtonDisabled: true});
        }
        this.setState({publishMaterialName: event.target.value})
    }

    handleChangePublishName(event: React.ChangeEvent<HTMLInputElement>): void {
        const fileList = this.state.disasterPreventionFileList;
        fileList[Number(event.target.name)].publishMaterialName = event.target.value;
        this.setState({disasterPreventionFileList: fileList});
    }

    handleChangeSortOrder(event: React.ChangeEvent<HTMLInputElement>): void {
        const fileList = this.state.disasterPreventionFileList;
        fileList[Number(event.target.name)].sortOrder = Number(event.target.value);
        this.setState({disasterPreventionFileList: fileList});
    }

    async handleChangeFile(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
        if (event.target.files && event.target.files[0] && event.target.files[0].name) {
            const fileName = event.target.files[0].name
            const fileSize = event.target.files[0].size
            const extension = fileName.split('.').pop()?.toLowerCase()
            const allowedExtension = 'pdf'

            const inputError = new RequestItemError(
                '',
                '',
                '',
                [],
            )
            if (fileSize > HKConstants.VMN.ASO.MAX_COMMENT_FILE_SIZE * 1024 * 1024) {
                inputError.message = Messages.EOPCA0015.toString(fileName, `${Math.floor(fileSize / (1024 * 1024))}MB`)
            }

            if (!extension || extension !== allowedExtension) {
                inputError.message = Messages.EOPCA0016.toString('PDF', fileName,
                    `${Math.floor(fileSize / (1024 * 1024))}MB`)
            }

            if (fileSize === 0) {
                inputError.message = Messages.EOPCA0017.toString(fileName, `${Math.floor(fileSize / (1024 * 1024))}MB`)
            }

            if (inputError.message) {
                this.props.eventEmitter.emit('disasterPreventionFile', inputError)
                if (this.disasterPreventionFileRef.current) {
                    this.disasterPreventionFileRef.current.value = ''
                }
                this.setState({
                    fileName: '',
                    fileData: '',
                    addFileButtonDisabled: true
                });
                return
            }

            const reader: FileReader = new FileReader();
            reader.onload = () => {
                this.setState({
                    fileName: fileName,
                    fileData: reader.result,
                    addFileButtonDisabled: !this.state.publishMaterialName
                })
            };
            reader.readAsDataURL(event.target.files[0]);
        }
    }

    updateRegisterButtonDisabled(): void {
        const {title, description, releaseSetting} = this.state

        let registerButtonDisabled = false
        if (!title || !description || !releaseSetting) {
            registerButtonDisabled = true
        }
        this.setState({registerButtonDisabled: registerButtonDisabled})
    }

    previewFile(dataUri: string): void {
        const data: string[] = dataUri.split(',');
        if (data[1]) {
            openPdfPreviewWindow(data[1])
        }
    }

    fileCount(): number {
        const disasterPreventionFileList = this.state.disasterPreventionFileList;
        return disasterPreventionFileList.filter(file => !file.deleteFlag).length;
    }

    deleteFile(index: number): void {
        const fileList = this.state.disasterPreventionFileList;
        fileList[index].deleteFlag = true;
        fileList[index].fileData = null;
        const addFileCount =
            fileList.filter(file => !file.registeredFlag && !file.deleteFlag);
        this.setState({
            disasterPreventionFileList: fileList,
            fileInputDisabled: (addFileCount.length >= 5)
        });
    }

    async addFileButton(): Promise<void> {
        const fileList = this.state.disasterPreventionFileList
        const validFileList = fileList.filter(file => !file.deleteFlag)
        const addFileList = validFileList.filter((file) => !file.registeredFlag)
        const registeredList = validFileList.filter((file) => file.registeredFlag)
        const disasterPreventionFile: DisasterPreventionFileList = {
            ['fileName']: this.state.fileName,
            ['fileData']: this.state.fileData,
            ['publishMaterialName']: this.state.publishMaterialName,
            ['sortOrder']: validFileList.length + 1,
            ['registeredFlag']: false,
            ['deleteFlag']: false
        };
        const validate = await this.validatePublishMaterialName()
        if (!validate) {
            return
        }
        fileList.push(disasterPreventionFile);
        this.setState({
            disasterPreventionFileList: fileList,
            fileName: '',
            fileData: '',
            publishMaterialName: '',
            addFileButtonDisabled: true,
            fileInputDisabled: (addFileList.length + registeredList.length + 1 >= 5)
        });
        if (this.disasterPreventionFileRef.current && this.disasterPreventionFileRef.current.value) {
            this.disasterPreventionFileRef.current.value = '';
        }
    }

    async validatePublishMaterialName(): Promise<boolean> {
        const {fileRepo} = this.props
        const fileName: ValidateUploadFile = {fileName: this.state.publishMaterialName}
        let requestItemErrors: RequestItemError[] | undefined
        await this.props.asyncCallFunction(
            fileRepo.validateUploadFile.bind(fileRepo, fileName),
            async (httpErrorOrNetworkError) => {
                requestItemErrors = httpErrorOrNetworkError.responseBody?.requestItemErrors ?? []
                requestItemErrors.forEach((item) => {
                    this.props.eventEmitter.emit('publishMaterialName', item)
                })
            },
        )
        return requestItemErrors === undefined;
    }

    async updateDisasterInfo(editParam: NatPutDisasterPreventionEditParams): Promise<boolean> {
        const {condominiumCd, disasterPreventionId, disasterRepo, eventEmitter} = this.props

        const response = await this.props.asyncCallFunction(
            disasterRepo.editDisasterPreventionInfo.bind(
                disasterRepo, condominiumCd, disasterPreventionId, editParam),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(eventEmitter)
            },
            {defaultErrorTitle: Messages.EOPCA0002.toString('防災情報')}
        )

        if (response && response.requestError === null) {
            return true
        }
        return false
    }

    async createDisasterInfoFile(
        requests: NatPostDisasterPreventionFileRequest[],
        disasterPreventionId: number
    ): Promise<string[]> {
        const {condominiumCd, disasterRepo, eventEmitter} = this.props

        let fileIndex: number = 1
        const fileCount: number = requests.length
        const responses: Nullable<NatPostDisasterPreventionFileResponse>[] = []

        for (const request of requests) {
            this.props.eventEmitter.changeLoadingMessage(`ファイルアップロード中... ${fileIndex}/${fileCount}`)

            const fileResponse = await this.props.asyncCallFunction(
                disasterRepo.createDisasterInfoFile.bind(disasterRepo, condominiumCd, disasterPreventionId, request),
                async (httpErrorOrNetworkError) => {
                    const requestItemErrors = httpErrorOrNetworkError.responseBody.requestItemErrors
                    requestItemErrors.forEach((item) => {
                        eventEmitter.emit(item.resultCode, item)
                    })
                }
            )
            fileIndex = fileIndex + 1
            responses.push(fileResponse)
        }

        return responses
            .map((file, index) => file?.requestItemErrors !== null ? requests[index].publishMaterialName : null)
            .filter((publishMaterialName): publishMaterialName is string => publishMaterialName !== null)

    }

    handleRegisterCancelClick(): void {
        this.props.eventEmitter.closeDialog();
    }

    async handleRegisterOkClick(): Promise<void> {
        const {condominiumCd, disasterPreventionId} = this.props
        const {
            title,
            description,
            releaseSetting,
            version,
            disasterPreventionFileList,
        } = this.state

        this.props.eventEmitter.closeDialog();
        await this.props.eventEmitter.showLoading();
        try {
            const newFileList = disasterPreventionFileList
                .filter((file) => !file.deleteFlag && !file.registeredFlag)

            const updateFileList = disasterPreventionFileList
                .filter((file) => !file.deleteFlag)
                .map((file) => ({
                    fileNumber: file.fileNumber,
                    publishMaterialName: file.publishMaterialName,
                    sortOrder: file.sortOrder
                }))

            const deleteFileList = disasterPreventionFileList
                .filter((deleteFile) =>
                    deleteFile.registeredFlag && deleteFile.deleteFlag && deleteFile.fileNumber !== undefined)
                .map((deleteFile) => ({
                    fileNumber: Number(deleteFile.fileNumber)
                }))

            const request: NatPutDisasterPreventionEditParams = {
                title: title,
                description: description,
                releaseSetting: releaseSetting,
                version: version,
                disasterPreventionFile: updateFileList,
                disasterPreventionDeleteFile: deleteFileList
            }
            const response = await this.updateDisasterInfo(request)
            if (response) {
                let initialMessage: HeadingAreaMessageParams
                initialMessage = {
                    title: null,
                    messageLevel: MESSAGE_TYPE.INFORMATION,
                    detail: Messages.IOPCC0005.toString('防災情報', this.state.title)
                }

                if (newFileList.length > 0) {
                    const fileRequests =
                        newFileList.map((file, index) => ({
                                fileName: file.fileName,
                                fileData: String(file.fileData).split(',')[1],
                                publishMaterialName: file.publishMaterialName,
                                sortOrder: file.sortOrder,
                                fileIndex: index
                            })
                        )

                    if (fileRequests.length > 0) {
                        const fileErrorResult =
                            await this.createDisasterInfoFile(fileRequests, disasterPreventionId)
                        if (fileErrorResult.length > 0) {
                            initialMessage = {
                                title: Messages.IOPCA0014.toString(),
                                messageLevel: MESSAGE_TYPE.ERROR,
                                detail: Messages.EOPCA0014.toStringWithId(),
                                itemErrors: fileErrorResult
                            }
                        }
                    }
                }
                this.props.navigate(
                    `/admin/condominiums/${condominiumCd}/disasters/preventions/${disasterPreventionId}`,
                    {
                        state: {
                            initialMessage: initialMessage
                        }
                    }
                )
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    async registerButton(): Promise<void> {
        await this.props.eventEmitter.showDialog({
                title: '確認',
                errorLevel: Messages.IOPCC0010.errorLevel,
                message: Messages.IOPCC0010.toString('防災情報'),
                buttonProps: [
                    {
                        isDefault: false,
                        buttonLabel: 'キャンセル',
                        buttonHandler: this.handleRegisterCancelClick
                    },
                    {
                        isDefault: false,
                        buttonLabel: 'OK',
                        buttonHandler: this.handleRegisterOkClick
                    }
                ]
            }
        );

    }

    renderFileArea(): ReactNode {
        return (
            <>
                {this.state.disasterPreventionFileList.map((file, index) => (
                    !file.deleteFlag ?
                        <TableRow key={index}>
                            <TableCell colSpan={3}>
                                <RowFlexContainer>
                                    <AlreadyDocumentFileWrapper>
                                        <AttachmentFileStyle>
                                            <FontAwesomeIcon icon={faFilePdf}/>
                                        </AttachmentFileStyle>
                                        {(file.fileData) ?
                                            <Link href={''}
                                                  onClick={(event: React.MouseEvent<HTMLElement>) => {
                                                      event.preventDefault();
                                                      this.previewFile(file.fileData as string)
                                                  }}>
                                                {file.publishMaterialName}
                                            </Link> :
                                            <Link href={file.path} target={'_blank'}>
                                                {file.publishMaterialName}
                                            </Link>
                                        }
                                    </AlreadyDocumentFileWrapper>
                                    <AlreadyDocumentActionWrapper>
                                        <TableCellLabelNoLeftPadding>
                                            並び順
                                        </TableCellLabelNoLeftPadding>
                                        <OrderWrapper>
                                            <DocumentFormWrapper>
                                                <StyledPPNumericField
                                                    InputProps={{
                                                        inputProps: {
                                                            min: 1,
                                                            max: 999
                                                        }
                                                    }}
                                                    fullWidth
                                                    name={String(index)}
                                                    onChange={this.handleChangeSortOrder}
                                                    value={file.sortOrder}
                                                    eventKeys={[`disasterPreventionFile[${index}].sortOrder`]}
                                                    eventEmitter={this.props.eventEmitter}
                                                />
                                            </DocumentFormWrapper>
                                        </OrderWrapper>
                                        <TableCellLabelNoLeftPadding>
                                            <DeleteLink onClick={(event: React.MouseEvent) => {
                                                event.preventDefault()
                                                this.deleteFile(index)
                                            }}>
                                                削除
                                            </DeleteLink>
                                        </TableCellLabelNoLeftPadding>
                                    </AlreadyDocumentActionWrapper>
                                </RowFlexContainer>
                            </TableCell>
                        </TableRow>
                        : null
                ))}
            </>
        )
    }

    renderInputFormTable(): ReactNode {
        const {condominiumName, title, description, releaseSetting} = this.state
        return (
            <>
                <Table>
                    <StyledTableCaption>防災情報</StyledTableCaption>
                    <TableBody>
                        {this.mode === ADMINISTORATOR_MODE.ALL.value &&
                            <TableRow>
                                <TableRowName>物件名</TableRowName>
                                <TableCell colSpan={4}>
                                    {condominiumName}
                                </TableCell>
                            </TableRow>
                        }
                        <TableRow>
                            <TableRowName required>タイトル</TableRowName>
                            <TableCell colSpan={4}>
                                <PPTextField
                                    InputProps={{
                                        inputProps: {
                                            name: 'title',
                                            maxLength: 50
                                        }
                                    }}
                                    required
                                    fullWidth
                                    value={title}
                                    onChange={this.handleChangeText}
                                    eventKeys={['title']}
                                    eventEmitter={this.props.eventEmitter}
                                />
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableRowName required>本文</TableRowName>
                            <TableCell colSpan={4}>
                                <PPHtmlEditor
                                    value={description}
                                    onEditorChange={this.handleChangeDescription}
                                    eventKeys={['description']}
                                    eventEmitter={this.props.eventEmitter}
                                />
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableRowName
                                optional
                                {...{
                                    rowSpan: this.fileCount() !== 0 ?
                                        this.fileCount() + 1 : 1
                                }}
                            >掲載資料</TableRowName>
                            <TableCell colSpan={2}>
                                <FlexStartRowFlexContainer>
                                    <DocumentWrapper>
                                        <DocumentFormWrapper>
                                            <PPFileInput
                                                InputProps={{
                                                    inputProps: {
                                                        id: 'disasterPreventionFile',
                                                        name: 'disasterPreventionFile'
                                                    }
                                                }}
                                                fullWidth
                                                inputRef={this.disasterPreventionFileRef}
                                                fileName={this.state.fileName}
                                                onChange={this.handleChangeFile}
                                                eventKeys={['disasterPreventionFile']}
                                                eventEmitter={this.props.eventEmitter}
                                                disabled={this.state.fileInputDisabled}
                                            />
                                        </DocumentFormWrapper>
                                    </DocumentWrapper>
                                    <DocumentNameNoteWrapper>
                                        1ファイル20MB、5ファイルまで
                                    </DocumentNameNoteWrapper>
                                </FlexStartRowFlexContainer>
                                <Box height={'16px'}/>
                                <FlexStartRowFlexContainer>
                                    <DocumentNameWrapper>
                                        <TableCellLabelTitle>掲載資料名</TableCellLabelTitle>
                                        <DocumentNameInputWrapper>
                                            <PPTextField
                                                InputProps={{
                                                    inputProps: {
                                                        name: 'publishMaterialName',
                                                        maxLength: 50
                                                    }
                                                }}
                                                required
                                                fullWidth
                                                value={this.state.publishMaterialName}
                                                onChange={this.handleChangePublishMaterialName}
                                                eventKeys={['publishMaterialName']}
                                                eventEmitter={this.props.eventEmitter}
                                                disabled={this.state.fileInputDisabled}
                                            />
                                        </DocumentNameInputWrapper>
                                    </DocumentNameWrapper>
                                    <DocumentAddButtonWrapper>
                                        <AdminPrimaryButton
                                            onClick={this.addFileButton}
                                            disabled={this.state.addFileButtonDisabled || this.state.fileInputDisabled}
                                        >PDFを追加
                                        </AdminPrimaryButton>
                                    </DocumentAddButtonWrapper>
                                </FlexStartRowFlexContainer>
                            </TableCell>
                        </TableRow>
                        {this.renderFileArea()}
                        <TableRow>
                            <TableRowName hint={Messages.HNTFA0005.toString()} required>公開区分</TableRowName>
                            <TableCell colSpan={4}>
                                <PPRadioGroup
                                    row
                                    data={DISASTER_RELEASE_SETTING.values()}
                                    value={releaseSetting}
                                    onChange={this.handleChangeReleaseSetting}
                                    eventKeys={['releaseSetting']}
                                    eventEmitter={this.props.eventEmitter}
                                />
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </>
        )
    }

    renderPageFooterArea(): ReactNode {
        const {condominiumCd, disasterPreventionId} = this.props
        return (
            <>
                <PageFotterAreaRight>
                    <AdminPrimaryButton
                        disabled={this.state.registerButtonDisabled}
                        onClick={this.registerButton}
                    >
                        登録
                    </AdminPrimaryButton>
                </PageFotterAreaRight>
                <PageFotterAreaLeft>
                    <BackLink
                        to={`/admin/condominiums/${condominiumCd}/disasters/preventions/${disasterPreventionId}`}
                    >
                        防災情報詳細へ戻る
                    </BackLink>
                </PageFotterAreaLeft>
            </>
        )
    }

    render(): ReactElement {
        if (!this.state.isLoadCompleted) {
            return (<></>)
        }
        return (
            <>
                <PageMainArea>
                    {this.renderInputFormTable()}
                </PageMainArea>
                <PageFooterArea>
                    {this.renderPageFooterArea()}
                </PageFooterArea>
            </>
        )
    }
}

NatAdminDisasterPreventionEditPage.contextType = AppAdminContext
export default createAdminPage(NatAdminDisasterPreventionEditPage);

const DocumentNameWrapper: StyledDiv = styled.div`
    display: flex;
    flex: 1 1 auto;
    align-items: baseline;
    padding-right: 15px;
`

const DocumentWrapper = styled.div`
    display: flex;
    flex: 1 0 auto;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    padding-right: 16px;
    max-width: 80%;
`

const DocumentNameNoteWrapper = styled.div`
    display: flex;
    flex: 1 0 auto;
    justify-content: flex-start;
    align-items: center;
    padding: 6px 0;
`

const DocumentNameInputWrapper = styled.div`
    width: 90%;
`

const DocumentFormWrapper: StyledDiv = styled.div`
    flex: 1 0 auto;
    width: 100%;
`

const DocumentAddButtonWrapper: StyledDiv = styled.div`
    flex: 0 0 auto;
`

const AlreadyDocumentWrapper = styled(DocumentNameWrapper)`
    flex: 1 1 0;
    align-items: center;
`

const AttachmentFileStyle = styled.div`
    font-size: 21px;
    line-height: 1;
    min-width: 21px;
    text-align: center;
    margin-right: 8px;
    color: ${Colors.AccentDark};
`

const AlreadyDocumentFileWrapper = styled(DocumentNameWrapper)`
    justify-content: flex-start;
`

const AlreadyDocumentActionWrapper = styled(DocumentNameWrapper)`
    justify-content: flex-end;
`

const OrderWrapper = styled(AlreadyDocumentWrapper)`
    min-width: fit-content;
    max-width: 20%;
`

const TableCellLabelNoLeftPadding: StyledLabel = styled.label`
    padding: 0 10px 0 0;
`

const TableCellLabelTitle: StyledLabel = styled.label`
    min-width: 80px;
    padding: 0 10px 0 0;
`

const StyledPPNumericField = styled(PPNumericField)`
    .MuiInput-root {
        .MuiInputBase-input {
            padding: 3px 0 4px;
        }
    }
`